/**
 * כרטיס מוצר בקטלוג — TASKS 6.2, 6.3ב · DESIGN.md 4.5, 5.3, 6.2.
 *
 * ⭐ Feedback1.md סעיף 8 (20.08.2026) — הכרטיס עבר מ-`PublicProductListItem`
 *    ל-`CatalogCard` (הקטלוג השטוח): מוצר בלי ואריאנטים = כרטיס אחד, מוצר עם
 *    ואריאנטים = כרטיס נפרד לכל ואריאנט. הקישור מעביר `?variant=<sku>` כדי
 *    ש-`ProductPage` יבחר את הוואריאנט הנכון מראש (סעיף 8.3).
 *
 * ⭐ כניסה: `whileInView` + fade/y/blur, `once: true` (6.2, 6.3 — כניסה חוזרת
 *    בכל גלילה נראית שבורה). ⭐ `layout` — כשרשימת המוצרים מסתננת (חיפוש),
 *    הכרטיסים הנשארים זזים חלק למקומם החדש במקום קפיצה (DESIGN.md 6.5).
 * ⭐ hover: הרמה `y: -6` (transform, `motion`) + `shadow-card-hover` (CSS
 *    רגיל — ראו `.product-card` ב-`index.css`) + קו נחושת תחתון שנגלה
 *    (`scaleX 0->1`) — `variants` על ההורה כדי ששלושת האפקטים ירוצו ביחד.
 * ⭐ `layoutId` על התמונה — shared element אל `ProductPage` (6.2 "קטלוג ->
 *    מוצר"). 🔴 **תוקן 20.08.2026 — באג אמיתי, דווח על ידי אריאנה: תמונות
 *    נעלמות בגלילה בקטלוג.** המפתח היה ה-SKU של המוצר בלבד — אבל בקטלוג
 *    השטוח (סעיף 8) מוצר עם וריאנטים מקבל **כמה כרטיסים** עם אותו SKU
 *    (למשל 6 כרטיסי `MG-0001`, 6 כרטיסי `TR-0003`). `motion` "מרנדר רק את
 *    האחרון" מבין אלמנטים עם אותו `layoutId` המורכבים בו-זמנית — בדיוק
 *    התסמין שדווח (תמונות נעלמות/מוחלפות בגלילה, ⛔ לא באג רשת/תמונה
 *    חסרה). המפתח עבר ל-`cardKey` (ייחודי לכל כרטיס — SKU-מזהה-וריאנט),
 *    ⚠️ **ויתור מכוון:** אנימציית ה-morph המשותפת אל `ProductPage` כבר
 *    לא תתאים ל-`layoutId` הקבוע שם (`product-image-${sku}`, בלי וריאנט)
 *    עבור כרטיסי וריאנט — `motion` פשוט לא ימצא התאמה ויעבור לדעיכה
 *    רגילה, ⛔ לא שגיאה ולא תקיעות (אותה מחלקת בעיה שכבר נפתרה ב-`ProductPage`
 *    עצמו, ראו ההערה שם) — תמונות שלא נעלמות עדיפות על אנימציה מושלמת.
 * ⭐ תמונה `object-contain` + `p-4` (⛔ לא `object-cover`) — תמונות המוצר הן
 *    צילומי מוצר על לבן (DESIGN.md 4.5), חיתוך שלהן בקרופ מרובע מסלף את
 *    הצילום. עקבי עם `HeroShowcase.tsx` שכבר עושה את אותו הדבר.
 */
import { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import type { CatalogCard } from '../publicApi/catalogApi.js';

interface ProductCardProps {
  card: CatalogCard;
  /** ⭐ אינדקס ברשימה — קובע את שהיית ה-stagger (DESIGN.md 6.2: 80ms). */
  index: number;
}

const CARD_VARIANTS = {
  rest: {},
  hover: { y: -6 },
};

const UNDERLINE_VARIANTS = {
  rest: { scaleX: 0 },
  hover: { scaleX: 1 },
};

export function ProductCard({ card, index }: ProductCardProps) {
  // ⭐ תמונה עם קובץ שבור בשרת (404) הייתה נעלמת לגמרי - ריבוע לבן על כרטיס
  // לבן, בלי שום סימן חזותי (`alt=""`). כאן, כמו ב-`HeroShowcase`, כשל טעינה
  // אמיתי נופל לאותה תצוגת "אין תמונה זמינה" שכבר קיימת לחוסר-תמונה.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = card.image && !imageFailed;

  const href = card.variantSku
    ? `/catalog/${card.categorySlug}/${card.sku}?variant=${encodeURIComponent(card.variantSku)}`
    : `/catalog/${card.categorySlug}/${card.sku}`;

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: (index % 12) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="list-none"
    >
      <motion.div
        initial="rest"
        whileHover="hover"
        whileFocus="hover"
        animate="rest"
        variants={CARD_VARIANTS}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="product-card relative flex h-full flex-col overflow-hidden rounded-lg border border-line bg-canvas"
      >
        <Link to={href} className="flex h-full flex-col text-ink">
          <div className="aspect-square w-full shrink-0 overflow-hidden bg-canvas p-4">
            {showImage ? (
              <motion.img
                layoutId={`product-image-${card.cardKey}`}
                src={card.image!.cardUrl}
                alt={card.image!.alt}
                loading="lazy"
                width={400}
                height={400}
                onError={() => setImageFailed(true)}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span className="sr-only">אין תמונה זמינה</span>
              </div>
            )}
          </div>
          {/* ⭐ `flex-1` — הכרטיס נמתח למלוא גובה השורה ברשת (ברירת המחדל של
              CSS Grid), ובלוק הטקסט תופס את מה שנשאר. כך שני מוצרים באותה
              שורה עם שם קצר וארוך (1 מול 2 שורות) מסתיימים באותו גובה כרטיס
              במקום קו תחתון מרוסק - הבאג שדווח בתמונה. */}
          <div className="flex flex-1 flex-col border-t border-line px-4 py-3">
            <p className="line-clamp-2 font-medium">{card.name}</p>
            {card.description && (
              <p className="text-muted mt-1 line-clamp-2 text-sm">{card.description}</p>
            )}
            <p className="ltr-inline tabular text-muted mt-1 text-sm">
              {card.variantSku ?? card.sku}
            </p>
          </div>
        </Link>

        <motion.span
          aria-hidden="true"
          variants={UNDERLINE_VARIANTS}
          transition={{ duration: 0.2 }}
          className="absolute inset-x-0 bottom-0 h-0.5 origin-center"
          style={{ backgroundColor: 'var(--color-copper)' }}
        />
      </motion.div>
    </motion.li>
  );
}
